import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePickerField } from "@/components/DatePickerField";
import { toast } from "sonner";

const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));
export const money = (v) => (v == null ? "—" : `${Number(v) < 0 ? "-" : ""}$${Math.abs(Number(v)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

export function BalanceCards({ b, sid }) {
  return (
    <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-5" data-testid={`supplier-balance-${sid}`}>
      {[["Total owed", b.total_owed_usd, "owed"], ["Total paid", b.total_paid_usd, "paid"], ["Remaining owed", b.remaining_owed_usd, "remaining"], ["Unapplied credit", b.unapplied_credit_usd, "credit"]].map(([l, v, k]) => (
        <div key={k}><p className="text-warmgrey">{l}</p><p className="num text-sm font-semibold text-charcoal" data-testid={`balance-${k}-${sid}`}>{money(v)}</p></div>
      ))}
      <div><p className="text-warmgrey">Payment progress</p><p className="num text-sm font-semibold text-charcoal" data-testid={`balance-progress-${sid}`}>{b.payment_progress_pct == null ? "—" : `${b.payment_progress_pct}%`}</p></div>
    </div>
  );
}

// ID-056: container cost & freight verification (an invoice match also verifies automatically)
export function FinanceVerification({ cid, onDone }) {
  const [v, setV] = useState(null);
  const [note, setNote] = useState("");
  const load = () => api.get(`/admin/containers/${cid}/finance-verification`).then(({ data }) => setV(data)).catch(() => setV({ valid: false, failed: true }));
  useEffect(() => { load(); }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps
  const verify = async () => {
    try { await api.post(`/admin/containers/${cid}/finance-verification`, { note }); toast.success("Cost & freight verified"); setNote(""); load(); onDone?.(); }
    catch (e) { err(e); }
  };
  if (!v) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="finance-verification-block">
      <span className={`badge-status ${v.valid ? "badge-available" : "badge-reserved"}`} data-testid="finance-verification-status">
        {v.valid ? "Cost & freight verified" : v.at ? "Verification out of date" : "Not verified by admin"}
      </span>
      {v.at && <span className="text-muted-foreground" data-testid="finance-verification-meta">{v.by_email} · {v.at.slice(0, 16).replace("T", " ")}</span>}
      {!v.valid && (
        <>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className="h-8 w-48 bg-white" data-testid="finance-verify-note" />
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={verify} data-testid="finance-verify-btn">Verify cost & freight</Button>
        </>
      )}
      <span className="w-full text-muted-foreground">A matching supplier invoice (with freight entered) also counts as verified. Any later cost or freight change requires re-verification.</span>
    </div>
  );
}

// ID-056: order-level supplier settlement status + Confirm Bank Receipt (manual wire/ACH only)
export function OrderSettlementCard({ orderId }) {
  const [s, setS] = useState(null);
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/admin/orders/${orderId}/settlement`).then(({ data }) => setS(data)).catch(() => {});
  useEffect(() => { load(); }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!s) return null;
  const confirm = async () => {
    setBusy(true);
    try { await api.post(`/admin/orders/${orderId}/bank-receipt`, { bank_reference: ref }); toast.success("Bank receipt confirmed"); load(); }
    catch (e) { err(e); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border border-border bg-white p-6" data-testid="order-settlement-card">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Supplier settlement</p>
      <p className="mt-2 text-xs text-warmgrey" data-testid="order-settlement-payment">{s.payment.label || (s.payment.reversal ? `Reversed (${s.payment.reversal.toLowerCase()})` : "—")}</p>
      {s.bank_receipt && <p className="mt-1 text-xs text-status-available" data-testid="order-bank-receipt">Bank receipt confirmed by {s.bank_receipt.by_email} · {s.bank_receipt.at.slice(0, 16).replace("T", " ")}{s.bank_receipt.bank_reference ? ` · ref ${s.bank_receipt.bank_reference}` : ""}</p>}
      {s.bank_receipt_required && !s.bank_receipt && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Bank reference (optional)" className="h-9 w-48 bg-white text-xs" data-testid="bank-receipt-ref" />
          <Button size="sm" className="h-9 bg-terracotta text-xs text-white hover:bg-terracotta-hover" disabled={busy} onClick={confirm} data-testid="bank-receipt-confirm">Confirm bank receipt</Button>
        </div>
      )}
      <div className="mt-3 space-y-1">
        {s.obligations.map((o) => (
          <p key={o.id} className="text-xs text-charcoal" data-testid={`order-obligation-${o.id}`}>
            {o.sku} · {o.dispatched_boxes} bx · <span className="font-medium">{o.status.replace(/_/g, " ").toLowerCase()}</span>
            {o.total_usd ? ` · ${money(o.total_usd)}` : ""}{o.blocker_labels.length ? ` — ${o.blocker_labels.join("; ")}` : ""}
          </p>
        ))}
        {s.obligations.length === 0 && <p className="text-xs text-muted-foreground" data-testid="order-obligations-empty">No supplier obligations — nothing dispatched yet.</p>}
      </div>
    </div>
  );
}

export function Banking({ sid }) {
  const EMPTY = { beneficiary_name: "", bank_name: "", swift_bic: "", account_number: "", currency: "USD", intermediary_bank: "", reason: "" };
  const [b, setB] = useState(null);
  const [f, setF] = useState(null);
  const load = () => api.get(`/admin/suppliers/${sid}/banking`).then(({ data }) => setB(data)).catch(() => {});
  useEffect(() => { load(); }, [sid]); // eslint-disable-line react-hooks/exhaustive-deps
  const edit = async () => {
    if (!b.on_file) { setF(EMPTY); return; }
    try { const { data } = await api.post(`/admin/suppliers/${sid}/banking/reveal`); setF({ ...EMPTY, ...Object.fromEntries(Object.entries(data).filter(([, v]) => v != null)), reason: "" }); } catch (e) { err(e); }
  };
  const save = async () => {
    try { const { data } = await api.put(`/admin/suppliers/${sid}/banking`, f); setB(data); setF(null); toast.success("Banking details saved"); } catch (e) { err(e); }
  };
  if (!b) return null;
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  return (
    <div className="space-y-2 text-xs" data-testid={`banking-${sid}`}>
      <p className="font-semibold uppercase tracking-wider text-warmgrey">Banking reference (masked · reference only, no transfers)</p>
      {b.on_file ? (
        <p className="text-charcoal" data-testid={`banking-masked-${sid}`}>{b.masked.beneficiary_name} · {b.masked.bank_name} · SWIFT {b.masked.swift_bic || "—"} · Acct {b.masked.account_number} · {b.masked.currency} · v{b.version}</p>
      ) : <p className="text-muted-foreground" data-testid={`banking-none-${sid}`}>No banking details on file.</p>}
      {!f && <Button size="sm" variant="outline" className="h-8 text-xs" onClick={edit} data-testid={`banking-edit-${sid}`}>{b.on_file ? "Edit (reveals, audited)" : "Add banking details"}</Button>}
      {f && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {[["beneficiary_name", "Beneficiary name"], ["bank_name", "Bank name"], ["swift_bic", "SWIFT/BIC"], ["account_number", "IBAN / RIB / account"], ["intermediary_bank", "Intermediary bank (optional)"]].map(([k, l]) => (
            <Input key={k} value={f[k] || ""} onChange={set(k)} placeholder={l} className="h-9 bg-white" data-testid={`banking-${k}`} />
          ))}
          <select value={f.currency} onChange={set("currency")} className="h-9 rounded-md border border-border bg-white px-2" data-testid="banking-currency">{["USD", "EUR", "MAD"].map((c) => <option key={c}>{c}</option>)}</select>
          {b.on_file && <Input value={f.reason} onChange={set("reason")} placeholder="Reason for change" className="h-9 bg-white sm:col-span-2" data-testid="banking-reason" />}
          <div className="flex gap-2"><Button size="sm" className="h-9 bg-terracotta text-white hover:bg-terracotta-hover" onClick={save} data-testid="banking-save">Save</Button><Button size="sm" variant="outline" className="h-9" onClick={() => setF(null)} data-testid="banking-cancel">Cancel</Button></div>
        </div>
      )}
      {b.history.length > 0 && <p className="text-muted-foreground" data-testid={`banking-history-${sid}`}>{b.history.map((h) => `v${h.version} ${h.at.slice(0, 10)} ${h.by_email || ""}${h.reason ? ` (${h.reason})` : ""}`).join(" · ")}</p>}
    </div>
  );
}

export function RecordPayment({ sid, onDone }) {
  const EMPTY = { amount: "", payment_date: new Date().toISOString().slice(0, 10), bank_reference: "", notes: "" };
  const [p, setP] = useState(EMPTY);
  const [preview, setPreview] = useState(null);
  const [key, setKey] = useState(newKey);
  const [file, setFile] = useState(null);
  const set = (k) => (e) => { setP((x) => ({ ...x, [k]: e.target.value })); if (k === "amount") setPreview(null); };
  const doPreview = async () => { try { const { data } = await api.post(`/admin/suppliers/${sid}/payments/preview`, { amount: p.amount }); setPreview(data); } catch (e) { err(e); } };
  const confirm = async () => {
    try {
      const { data } = await api.post(`/admin/suppliers/${sid}/payments`, { ...p, currency: "USD", idempotency_key: key });
      if (file) { const fd = new FormData(); fd.append("file", file); await api.post(`/admin/supplier-payments/${data.id}/receipt`, fd); }
      toast.success("Payment recorded (no transfer initiated)"); setP(EMPTY); setPreview(null); setFile(null); setKey(newKey()); onDone();
    } catch (e) { err(e); }
  };
  return (
    <div className="space-y-2 text-xs" data-testid={`record-payment-${sid}`}>
      <p className="font-semibold uppercase tracking-wider text-warmgrey">Record supplier payment (made outside the app)</p>
      <div className="flex flex-wrap gap-2">
        <select value="USD" disabled className="h-9 rounded-md border border-border bg-white px-2" data-testid="payment-currency"><option>USD</option></select>
        <Input type="number" min="0" step="0.01" value={p.amount} onChange={set("amount")} placeholder="Amount USD" className="h-9 w-36 bg-white" data-testid="payment-amount" />
        <div className="w-44"><DatePickerField value={p.payment_date} onChange={(v) => set("payment_date")({ target: { value: v } })} className="h-9" placeholder="Payment date" testId="payment-date" /></div>
        <Input value={p.bank_reference} onChange={set("bank_reference")} placeholder="Bank reference" className="h-9 w-44 bg-white" data-testid="payment-ref" />
        <Input value={p.notes} onChange={set("notes")} placeholder="Notes (optional)" className="h-9 w-48 bg-white" data-testid="payment-notes" />
        <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="h-9 text-xs" data-testid="payment-receipt" />
        <Button size="sm" variant="outline" className="h-9 text-xs" disabled={!p.amount} onClick={doPreview} data-testid="payment-preview-btn">Preview allocation</Button>
      </div>
      {preview && (
        <div className="rounded-md bg-sand/40 p-3" data-testid="payment-preview">
          {preview.allocations.map((a) => <p key={a.obligation_id} data-testid={`preview-row-${a.obligation_id}`}>{a.eligible_at?.slice(0, 10)} · {a.order_number} · {a.sku} · open {money(a.open_usd)} → apply <span className="font-semibold">{money(a.apply_usd)}</span></p>)}
          {preview.allocations.length === 0 && <p>No eligible unpaid obligations.</p>}
          <p className="mt-1" data-testid="preview-credit">Unapplied supplier credit: {money(preview.unapplied_credit_usd)}</p>
          <Button size="sm" className="mt-2 h-8 bg-terracotta text-xs text-white hover:bg-terracotta-hover" disabled={!p.bank_reference || !p.payment_date} onClick={confirm} data-testid="payment-confirm-btn">Confirm & record payment</Button>
        </div>
      )}
    </div>
  );
}

// ID-056: compact supplier settlement block inside the existing Suppliers row (no dashboard — Phase 3)
export function SupplierSettlement({ sid }) {
  const [b, setB] = useState(null);
  const load = () => api.get(`/admin/suppliers/${sid}/balance`).then(({ data }) => setB(data)).catch(() => {});
  useEffect(() => { load(); }, [sid]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!b) return null;
  return (
    <div className="mt-3 space-y-4 rounded-md border border-border bg-white p-4" data-testid={`supplier-settlement-${sid}`}>
      <BalanceCards b={b} sid={sid} />
      <p className="text-[11px] text-muted-foreground" data-testid={`balance-pending-${sid}`}>{b.pending.count} obligation(s) pending calculation/eligibility (not in balance) · {b.note}</p>
      <RecordPayment sid={sid} onDone={load} />
      <Banking sid={sid} />
    </div>
  );
}
